import React from 'react';
import { H5 } from '../../../../AbstractElements';
import { FormGroup, Input, Label, FormFeedback, Row, Col, InputGroup, Button } from 'reactstrap';
import { Search } from 'react-feather';
import PropTypes from 'prop-types';

const AccidentInfoForm = ({ formData, errors, setFormData, handleInputChange, handleLicensePlateSearch, fieldPermissions = [], canEdit = true }) => {
    const formatLicensePlate = (value) => {
        // 1. Sadece harf ve rakamları al, büyük harfe çevir
        const cleanedValue = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        if (!cleanedValue) return '';

        // 2. Harf ve rakam gruplarını bul (örneğin, "34", "ABC", "123")
        const parts = cleanedValue.match(/[A-Z]+|[0-9]+/g);

        // 3. Eğer grup bulunamazsa (çok olası değil ama kontrol edelim) temizlenmiş değeri döndür
        if (!parts) return cleanedValue;

        // 4. Grupları boşlukla birleştir
        return parts.join(' ');
    };

    // Helper to disable plate search button (optional, based on format or length)
    const isPlateSearchDisabled = () => {
        // Example: Disable if plate is empty or maybe too short
        return !formData.licensePlate || formData.licensePlate.trim().length < 3; // Adjust minimum length as needed
    };

    // Helper function to get field access properties
    const getFieldAccess = (fieldName) => {
        // If canEdit is false, all fields should be disabled regardless of permissions
        if (!canEdit) {
            return {
                editable: false,
                required: false,
            };
        }
        
        const permission = fieldPermissions.find(p => p.fieldName === fieldName);
        return {
            editable: permission ? permission.access === 'Editable' : true, // Default to editable
            required: permission ? permission.required : false, // Default to not required
        };
    };

    // Map form fields to API field names and get access rules
    const licensePlateAccess = getFieldAccess('licenseplates.licenseplatenumber');
    const chassisNumberAccess = getFieldAccess('licenseplates.carchasisnumber'); // Assuming API field name
    const carBrandAccess = getFieldAccess('licenseplates.carbrand');
    const carModelAccess = getFieldAccess('licenseplates.carmodel');
    const carYearAccess = getFieldAccess('licenseplates.caryear');
    const customerContractRateAccess = getFieldAccess('customers.contractrate'); // Updated field name
    const willLorAccess = getFieldAccess('accidents.willlorbeworked');
    const lovClaimAccess = getFieldAccess('licenseplates.istherelovclaim');
    const carSoldAccess = getFieldAccess('licenseplates.hascarsoldbefore');
    const notaryFeeAccess = getFieldAccess('accidents.shouldpaynotaryfee');
    const notaryAmountAccess = getFieldAccess('accidents.notaryfeeamount');
    const estDepreciationAccess = getFieldAccess('accidents.estimateddepreciationamount');
    const damageAmountAccess = getFieldAccess('accidents.damageamount');
    const damageDiffAccess = getFieldAccess('accidents.damagedifferenceamount');
    const lorAmountAccess = getFieldAccess('accidents.loramount'); // Assuming API field name
    const accidentDateAccess = getFieldAccess('accidents.accidentdate');
    const ourFaultRateAccess = getFieldAccess('accidents.ourfaultrate');
    const trialDocAccess = getFieldAccess('accidents.istrialdocument');
    const workDocAccess = getFieldAccess('accidents.isworkdocument');
    const lovClaimAmountAccess = getFieldAccess('accidents.lovclaimamount');
    const lpImpeachmentStatusAccess = getFieldAccess('accidents.lpimpeachmentstatus');
    const lpImpeachmentAmountAccess = getFieldAccess('accidents.lpimpeachmentamount');

    return (
        <>
            <H5>Kaza Bilgileri</H5>
            <div style={{ height: "20px" }}></div>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label for="licensePlate">
                            Plaka {licensePlateAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <InputGroup>
                            <Input
                                type="text"
                                id="licensePlate"
                                name="licensePlate"
                                value={formData.licensePlate || ''}
                                disabled={!licensePlateAccess.editable} // Use API permission
                                onChange={(e) => {
                                    const formattedValue = formatLicensePlate(e.target.value);
                                    setFormData(prev => ({
                                        ...prev,
                                        licensePlate: formattedValue || null
                                    }));
                                }}
                                invalid={!!errors.licensePlate}
                                placeholder="Örn: 34 ABC 123"
                            />
                            <Button
                                color="primary"
                                outline
                                onClick={() => handleLicensePlateSearch(formData.licensePlate)}
                                // Disable search if field not editable or based on existing logic
                                disabled={!licensePlateAccess.editable || isPlateSearchDisabled()}
                                aria-label="Plaka ile Ara"
                            >
                                <Search size={16} />
                            </Button>
                            <FormFeedback style={{ display: !!errors.licensePlate ? 'block' : 'none' }}>
                                {errors.licensePlate || 'Plaka formatı otomatik düzeltilir.'}
                            </FormFeedback>
                        </InputGroup>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="chassisNumber">
                            Şase Numarası {chassisNumberAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="chassisNumber"
                            name="chassisNumber"
                            value={formData.chassisNumber || ''}
                            disabled={!chassisNumberAccess.editable} // Use API permission
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 17).trim();
                                handleInputChange({
                                    target: {
                                        name: 'chassisNumber',
                                        value: value || null
                                    }
                                });
                            }}
                            maxLength={17}
                            pattern="[A-Z0-9]{17}"
                            invalid={!!errors.chassisNumber || (formData.chassisNumber && formData.chassisNumber.length !== 17)}
                        />
                        <FormFeedback>
                            {errors.chassisNumber || (formData.chassisNumber && formData.chassisNumber.length !== 17 ? 'Şase Numarası 17 karakter olmalıdır' : '')}
                        </FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label for="carBrand">
                            Araç Markası {carBrandAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="carBrand"
                            name="carBrand"
                            value={formData.carBrand || ''}
                            disabled={!carBrandAccess.editable} // Use API permission
                            onChange={(e) => {
                                const value = e.target.value
                                    .replace(/^\s+/g, '')
                                    .replace(/\s{2,}/g, ' ')
                                    .split(' ')
                                    .map(word => word ? word.charAt(0).toUpperCase() + word.slice(1) : '')
                                    .join(' ');
                                handleInputChange({
                                    target: {
                                        name: 'carBrand',
                                        value: value || null
                                    }
                                });
                            }}
                            invalid={!!errors.carBrand}
                        />
                        <FormFeedback>{errors.carBrand}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label for="carModel">
                            Araç Modeli {carModelAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="carModel"
                            name="carModel"
                            value={formData.carModel || ''}
                            disabled={!carModelAccess.editable} // Use API permission
                            onChange={(e) => {
                                const value = e.target.value
                                    .replace(/^\s+/g, '')
                                    .replace(/\s{2,}/g, ' ')
                                    .split(' ')
                                    .map(word => word ? word.charAt(0).toUpperCase() + word.slice(1) : '')
                                    .join(' ');
                                handleInputChange({
                                    target: {
                                        name: 'carModel',
                                        value: value || null
                                    }
                                });
                            }}
                            invalid={!!errors.carModel}
                        />
                        <FormFeedback>{errors.carModel}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label for="carYear">
                            Araç Yılı {carYearAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="carYear"
                            name="carYear"
                            value={formData.carYear || ''}
                            disabled={!carYearAccess.editable} // Use API permission
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4).trim();
                                const currentYear = new Date().getFullYear();
                                if (!value || parseInt(value) <= currentYear) {
                                    handleInputChange({
                                        target: {
                                            name: 'carYear',
                                            value: value || null
                                        }
                                    });
                                }
                            }}
                            maxLength={4}
                            placeholder={`1900 - ${new Date().getFullYear()}`}
                            invalid={!!errors.carYear || (formData.carYear && (parseInt(formData.carYear) > new Date().getFullYear() || parseInt(formData.carYear) < 1900))}
                        />
                        <FormFeedback>
                            {errors.carYear || (formData.carYear && (parseInt(formData.carYear) > new Date().getFullYear() || parseInt(formData.carYear) < 1900) ? `Araç yılı 1900 ile ${new Date().getFullYear()} arasında olmalıdır` : '')}
                        </FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <FormGroup>
                <Label for="customerContractRate">
                    Sözleşme Oranı (%) {customerContractRateAccess.required && <span className="text-danger">*</span>}
                </Label>
                <Input
                    type="text"
                    id="customerContractRate"
                    name="customerContractRate"
                    value={formData.customerContractRate || ''}
                    disabled={!customerContractRateAccess.editable} // Use API permission
                    onChange={(e) => {
                        let value = e.target.value;
                        // Sadece sayısal değerlere izin ver
                        if (value && !/^\d*$/.test(value)) {
                            value = value.replace(/[^\d]/g, '');
                        }
                        // 0-100 aralığında kontrol
                        if (value && parseInt(value) > 100) {
                            value = '100';
                        }
                        handleInputChange({
                            target: {
                                name: 'customerContractRate',
                                value: value || null
                            }
                        });
                    }}
                    placeholder="0-100"
                    invalid={!!errors.customerContractRate}
                />
                <FormFeedback>{errors.customerContractRate}</FormFeedback>
            </FormGroup>

            <div style={{ height: "20px" }}></div>

            {/* Moved Document Type Radio Buttons Here */}
            <FormGroup className="d-flex align-items-center mb-4">
                <div className="d-flex">
                    <div className="radio radio-primary me-3 d-flex align-items-center">
                        <Input
                            id="isTrialDocumentTrue"
                            type="radio"
                            name="documentType"
                            value="trial"
                            checked={formData.isTrialDocument === "yes"}
                            disabled={!trialDocAccess.editable}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    isTrialDocument: "yes",
                                    isWorkDocument: "no",
                                }))
                            }
                            invalid={!!errors.documentType}
                        />
                        <Label for="isTrialDocumentTrue" className="ms-1 mb-0">
                            Deneme Dosyası
                        </Label>
                    </div>
                    <div className="radio radio-primary me-3 d-flex align-items-center">
                        <Input
                            id="isWorkDocumentTrue"
                            type="radio"
                            name="documentType"
                            value="work"
                            checked={formData.isWorkDocument === "yes" || (formData.isTrialDocument !== "yes" && formData.isWorkDocument !== "yes")}
                            disabled={!workDocAccess.editable}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    isTrialDocument: "no",
                                    isWorkDocument: "yes",
                                }))
                            }
                            invalid={!!errors.documentType}
                        />
                        <Label for="isWorkDocumentTrue" className="ms-1 mb-0">
                            İş Dosyası
                        </Label>
                    </div>
                    <FormFeedback>{errors.documentType}</FormFeedback>
                </div>
            </FormGroup>

            {/* Grid sistemi ile hizalama yapıldı */}
            <FormGroup className="mb-3">
                <Row className="align-items-center">
                    <Col xl={4} md={6}>
                        <Label for="hasCarSoldBeforeRadio" className="text-nowrap mb-0">
                            Önceden Araç Satış Var Mı? {carSoldAccess.required && <span className="text-danger">*</span>}
                        </Label>
                    </Col>
                    <Col xl={8} md={6} className='mt-md-0 mt-2'>
                        <div className="d-flex">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="hasCarSoldBeforeTrue"
                                    type="radio"
                                    name="hasCarSoldBefore"
                                    value="yes"
                                    checked={formData.hasCarSoldBefore === "yes"}
                                    disabled={!carSoldAccess.editable} // Use API permission
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            hasCarSoldBefore: "yes",
                                        }))
                                    }
                                    invalid={!!errors.hasCarSoldBefore}
                                />
                                <Label for="hasCarSoldBeforeTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="hasCarSoldBeforeFalse"
                                    type="radio"
                                    name="hasCarSoldBefore"
                                    value="no"
                                    checked={formData.hasCarSoldBefore === "no" || formData.hasCarSoldBefore === null}
                                    disabled={!carSoldAccess.editable} // Use API permission
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            hasCarSoldBefore: "no",
                                        }))
                                    }
                                    invalid={!!errors.hasCarSoldBefore}
                                />
                                <Label for="hasCarSoldBeforeFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                        </div>
                        <FormFeedback style={{ display: errors.hasCarSoldBefore ? 'block' : 'none' }}>{errors.hasCarSoldBefore}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            {/* --- Moved Değer Kaybı Başvurusu Radio Button Group --- */}
            <FormGroup className="mb-3">
                <Row className="align-items-center">
                    <Col xl={4} md={6}>
                        <Label for="isThereLovClaimRadio" className="text-nowrap mb-0">
                            Önceden Değer Kaybı Başvurusu Var Mı? {lovClaimAccess.required && <span className="text-danger">*</span>}
                        </Label>
                    </Col>
                    <Col xl={8} md={6} className='mt-md-0 mt-2'>
                        <div className="d-flex">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="isThereLovClaimTrue"
                                    type="radio"
                                    name="isThereLovClaim"
                                    value="yes"
                                    checked={formData.isThereLovClaim === "yes"}
                                    disabled={!lovClaimAccess.editable} // Use API permission
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            isThereLovClaim: "yes",
                                        }))
                                    }
                                    invalid={!!errors.isThereLovClaim}
                                />
                                <Label for="isThereLovClaimTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="isThereLovClaimFalse"
                                    type="radio"
                                    name="isThereLovClaim"
                                    value="no"
                                    checked={formData.isThereLovClaim === "no" || formData.isThereLovClaim === null}
                                    disabled={!lovClaimAccess.editable} // Use API permission
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            isThereLovClaim: "no",
                                            lovClaimAmount: null, // <-- Reset amount when 'No' is selected
                                        }))
                                    }
                                    invalid={!!errors.isThereLovClaim}
                                />
                                <Label for="isThereLovClaimFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                        </div>
                        <FormFeedback style={{ display: errors.isThereLovClaim ? 'block' : 'none' }}>{errors.isThereLovClaim}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>
            {/* --- End Moved --- */}

            {/* --- UPDATED: Conditional Input for Prior Payment Amount --- */}
            {formData.isThereLovClaim === "yes" && (
                <Row> {/* No longer offset, standard row */}
                    <Col md={4}> {/* Match the column size of notaryFeeAmount */}
                        <FormGroup>
                            {/* Renamed Label */}
                            <Label for="lovClaimAmount">
                                Önceden Alınan Ödeme Tutarı {lovClaimAmountAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text" // Use text to handle decimal formatting manually
                                id="lovClaimAmount"
                                name="lovClaimAmount"
                                value={formData.lovClaimAmount ?? ''}
                                disabled={!lovClaimAmountAccess.editable}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    // Allow only numbers and one decimal point
                                    value = value.replace(/[^0-9.]/g, '');
                                    const decimalCheck = value.split('.');
                                    if (decimalCheck.length > 2) {
                                        value = decimalCheck[0] + '.' + decimalCheck.slice(1).join('');
                                    }
                                    // Optional: Limit decimal places
                                    if (decimalCheck[1] && decimalCheck[1].length > 2) {
                                        value = decimalCheck[0] + '.' + decimalCheck[1].slice(0, 2);
                                    }

                                    handleInputChange({
                                        target: {
                                            name: 'lovClaimAmount',
                                            // Pass null if empty, otherwise parse as float
                                            value: value === '' ? null : parseFloat(value)
                                        }
                                    });
                                }}
                                placeholder="0.00"
                                invalid={!!errors.lovClaimAmount}
                            />
                            <FormFeedback>{errors.lovClaimAmount}</FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>
            )}
            {/* --- End UPDATED --- */}

            {/* --- NEW: Plaka Azil Durumu Radio Button Group --- */}
            <FormGroup className="mb-3">
                <Row className="align-items-center">
                    <Col xl={4} md={6}>
                        <Label for="lpImpeachmentStatusRadio" className="text-nowrap mb-0">
                            Plakada Azil Var Mı? {lpImpeachmentStatusAccess.required && <span className="text-danger">*</span>}
                        </Label>
                    </Col>
                    <Col xl={8} md={6} className='mt-md-0 mt-2'>
                        <div className="d-flex">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="lpImpeachmentStatusTrue"
                                    type="radio"
                                    name="lpImpeachmentStatus"
                                    value="yes"
                                    // Check against true boolean from API or "yes" string from form interaction
                                    checked={formData.lpImpeachmentStatus === true || formData.lpImpeachmentStatus === "yes"}
                                    disabled={!lpImpeachmentStatusAccess.editable}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            lpImpeachmentStatus: "yes", // Set as string for consistency in form state
                                        }))
                                    }
                                    invalid={!!errors.lpImpeachmentStatus}
                                />
                                <Label for="lpImpeachmentStatusTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="lpImpeachmentStatusFalse"
                                    type="radio"
                                    name="lpImpeachmentStatus"
                                    value="no"
                                     // Check against false boolean, null, undefined, or "no" string
                                    checked={formData.lpImpeachmentStatus === false || formData.lpImpeachmentStatus === null || formData.lpImpeachmentStatus === undefined || formData.lpImpeachmentStatus === "no"}
                                    disabled={!lpImpeachmentStatusAccess.editable}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            lpImpeachmentStatus: "no", // Set as string for consistency
                                            lpImpeachmentAmount: null, // Reset amount when 'No' is selected
                                        }))
                                    }
                                    invalid={!!errors.lpImpeachmentStatus}
                                />
                                <Label for="lpImpeachmentStatusFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                        </div>
                        <FormFeedback style={{ display: errors.lpImpeachmentStatus ? 'block' : 'none' }}>{errors.lpImpeachmentStatus}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>
            {/* --- End NEW --- */}

            {/* --- NEW: Conditional Input for Plaka Azil Tutarı --- */}
            {(formData.lpImpeachmentStatus === "yes" || formData.lpImpeachmentStatus === true) && ( // Check for string "yes" or boolean true
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="lpImpeachmentAmount">
                                Plaka Azil Tutarı {lpImpeachmentAmountAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text" // Use text for decimal handling
                                id="lpImpeachmentAmount"
                                name="lpImpeachmentAmount"
                                value={formData.lpImpeachmentAmount ?? ''}
                                disabled={!lpImpeachmentAmountAccess.editable}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    // Allow only numbers and one decimal point
                                    value = value.replace(/[^0-9.]/g, '');
                                    const decimalCheck = value.split('.');
                                    if (decimalCheck.length > 2) {
                                        value = decimalCheck[0] + '.' + decimalCheck.slice(1).join('');
                                    }
                                    // Optional: Limit decimal places
                                    if (decimalCheck[1] && decimalCheck[1].length > 2) {
                                        value = decimalCheck[0] + '.' + decimalCheck[1].slice(0, 2);
                                    }

                                    handleInputChange({
                                        target: {
                                            name: 'lpImpeachmentAmount',
                                            // Pass null if empty, otherwise parse as float
                                            value: value === '' ? null : parseFloat(value)
                                        }
                                    });
                                }}
                                placeholder="0.00"
                                invalid={!!errors.lpImpeachmentAmount}
                            />
                            <FormFeedback>{errors.lpImpeachmentAmount}</FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>
            )}
            {/* --- End NEW --- */}

            <FormGroup className="mb-3">
                <Row className="align-items-center">
                    <Col xl={4} md={6}>
                        <Label for="shouldPayNotaryFeeRadio" className="text-nowrap mb-0">
                            Noter Ücreti Ödenecek Mi? {notaryFeeAccess.required && <span className="text-danger">*</span>}
                        </Label>
                    </Col>
                    <Col xl={8} md={6} className='mt-md-0 mt-2'>
                        <div className="d-flex">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="shouldPayNotaryFeeTrue"
                                    type="radio"
                                    name="shouldPayNotaryFee"
                                    value="yes"
                                    checked={formData.shouldPayNotaryFee === "yes"}
                                    disabled={!notaryFeeAccess.editable} // Use API permission
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            shouldPayNotaryFee: "yes",
                                        }))
                                    }
                                    invalid={!!errors.shouldPayNotaryFee}
                                />
                                <Label for="shouldPayNotaryFeeTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="shouldPayNotaryFeeFalse"
                                    type="radio"
                                    name="shouldPayNotaryFee"
                                    value="no"
                                    checked={formData.shouldPayNotaryFee === "no" || formData.shouldPayNotaryFee === null}
                                    disabled={!notaryFeeAccess.editable} // Use API permission
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            shouldPayNotaryFee: "no",
                                            notaryFeeAmount: null,
                                        }))
                                    }
                                    invalid={!!errors.shouldPayNotaryFee}
                                />
                                <Label for="shouldPayNotaryFeeFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                        </div>
                        <FormFeedback style={{ display: errors.shouldPayNotaryFee ? 'block' : 'none' }}>{errors.shouldPayNotaryFee}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            {formData.shouldPayNotaryFee === "yes" && (
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="notaryFeeAmount">
                                Notere Ödenecek Tutar {notaryAmountAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text"
                                id="notaryFeeAmount"
                                name="notaryFeeAmount"
                                // Use ?? '' to handle null/undefined for text inputs that represent numbers
                                value={formData.notaryFeeAmount ?? ''}
                                disabled={!notaryAmountAccess.editable} // Use API permission
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    handleInputChange({
                                        target: {
                                            name: 'notaryFeeAmount',
                                            // Pass 0 if empty, otherwise the number
                                            value: value ? Number(value) : 0 
                                        }
                                    });
                                }}
                                placeholder="0"
                                invalid={!!errors.notaryFeeAmount}
                            />
                            <FormFeedback>{errors.notaryFeeAmount}</FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>
            )}

            {/* "Hak Mahrumiyeti Çalışılacak Mı?" moved here */}
            <FormGroup className="mb-3">
                <Row className="align-items-center">
                    <Col xl={4} md={6}>
                        <Label for="willLorBeWorkedRadio" className="text-nowrap mb-0">
                            Hak Mahrumiyeti Çalışılacak Mı? {willLorAccess.required && <span className="text-danger">*</span>}
                        </Label>
                    </Col>
                    <Col xl={8} md={6} className='mt-md-0 mt-2'>
                        <div className="d-flex">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="willLorBeWorkedTrue"
                                    type="radio"
                                    name="willLorBeWorked"
                                    value="yes"
                                    checked={formData.willLorBeWorked === "yes"}
                                    disabled={!willLorAccess.editable} // Use API permission
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            willLorBeWorked: "yes",
                                        }))
                                    }
                                    invalid={!!errors.willLorBeWorked}
                                />
                                <Label for="willLorBeWorkedTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="willLorBeWorkedFalse"
                                    type="radio"
                                    name="willLorBeWorked"
                                    value="no"
                                    checked={formData.willLorBeWorked === "no" || formData.willLorBeWorked === null}
                                    disabled={!willLorAccess.editable} // Use API permission
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            willLorBeWorked: "no",
                                        }))
                                    }
                                    invalid={!!errors.willLorBeWorked}
                                />
                                <Label for="willLorBeWorkedFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                        </div>
                        <FormFeedback style={{ display: errors.willLorBeWorked ? 'block' : 'none' }}>{errors.willLorBeWorked}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            <Row>
                <Col md={formData.willLorBeWorked === "yes" ? 3 : 4}> {/* Adjusted md size */}
                    <FormGroup>
                        <Label for="estimatedDepreciationAmount">
                            Değer Kaybı Tutarı {estDepreciationAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="estimatedDepreciationAmount"
                            name="estimatedDepreciationAmount"
                            // Use ?? ''
                            value={formData.estimatedDepreciationAmount ?? ''}
                            disabled={!estDepreciationAccess.editable} // Use API permission
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                handleInputChange({
                                    target: {
                                        name: 'estimatedDepreciationAmount',
                                        value: value ? Number(value) : 0 
                                    }
                                });
                            }}
                            placeholder="0"
                            invalid={!!errors.estimatedDepreciationAmount}
                        />
                        <FormFeedback>{errors.estimatedDepreciationAmount}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={formData.willLorBeWorked === "yes" ? 3 : 4}> {/* Adjusted md size */}
                    <FormGroup>
                        <Label for="damageAmount">
                            Hasar Tutarı {damageAmountAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="damageAmount"
                            name="damageAmount"
                            // Use ?? ''
                            value={formData.damageAmount ?? ''}
                            disabled={!damageAmountAccess.editable} // Use API permission
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                handleInputChange({
                                    target: {
                                        name: 'damageAmount',
                                        value: value ? Number(value) : 0 
                                    }
                                });
                            }}
                            placeholder="0"
                            invalid={!!errors.damageAmount}
                        />
                        <FormFeedback>{errors.damageAmount}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={formData.willLorBeWorked === "yes" ? 3 : 4}> {/* Adjusted md size */}
                    <FormGroup>
                        <Label for="damageDifferenceAmount">
                            Hasar Farkı Tutarı {damageDiffAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="damageDifferenceAmount"
                            name="damageDifferenceAmount"
                            // Use ?? ''
                            value={formData.damageDifferenceAmount ?? ''}
                            disabled={!damageDiffAccess.editable} // Use API permission
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                handleInputChange({
                                    target: {
                                        name: 'damageDifferenceAmount',
                                        value: value ? Number(value) : 0 
                                    }
                                });
                            }}
                            placeholder="0"
                            invalid={!!errors.damageDifferenceAmount}
                        />
                        <FormFeedback>{errors.damageDifferenceAmount}</FormFeedback>
                    </FormGroup>
                </Col>
                {formData.willLorBeWorked === "yes" && ( /* Changed condition */
                    <Col md={3}>
                        <FormGroup>
                            <Label for="lorAmount">
                                Hak Mahrumiyeti Tutarı {lorAmountAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text"
                                id="lorAmount"
                                name="lorAmount"
                                // Use ?? ''
                                value={formData.lorAmount ?? ''}
                                disabled={!lorAmountAccess.editable} // Use API permission
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    handleInputChange({
                                        target: {
                                            name: 'lorAmount',
                                            value: value ? Number(value) : 0 
                                        }
                                    });
                                }}
                                placeholder="0"
                                invalid={!!errors.lorAmount}
                            />
                            <FormFeedback>{errors.lorAmount}</FormFeedback>
                        </FormGroup>
                    </Col>
                )}
            </Row>

            <FormGroup>
                <Label for="accidentDate">
                    Kaza Tarihi {accidentDateAccess.required && <span className="text-danger">*</span>}
                </Label>
                <Input
                    type="datetime-local"
                    id="accidentDate"
                    name="accidentDate"
                    value={formData.accidentDate instanceof Date && !isNaN(formData.accidentDate)
                        ? formData.accidentDate.toISOString().slice(0, 16)
                        : ''}
                    disabled={!accidentDateAccess.editable} // Use API permission
                    onChange={(e) => {
                        const value = e.target.value ? new Date(e.target.value) : null;
                        handleInputChange({
                            target: {
                                name: 'accidentDate',
                                value
                            }
                        });
                    }}
                    invalid={!!errors.accidentDate}
                />
                <FormFeedback>{errors.accidentDate}</FormFeedback>
            </FormGroup>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label for="ourFaultRate">
                            Müvekkil Kusur Oranı {ourFaultRateAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <div className="input-group">
                            <Input
                                type="text"
                                id="ourFaultRate"
                                name="ourFaultRate"
                                // Use || ''
                                value={formData.ourFaultRate || ''}
                                disabled={!ourFaultRateAccess.editable} // Use API permission
                                onChange={(e) => {
                                    let value = e.target.value.replace(/[^0-9]/g, '');
                                    // 100'den büyük değer girilmesini engelle
                                    if (value && parseInt(value) > 100) {
                                        value = '100';
                                    }

                                    // Update client fault rate
                                    handleInputChange({
                                        target: {
                                            name: 'ourFaultRate',
                                            value: value || null 
                                        }
                                    });

                                    // Automatically calculate counterparty fault rate
                                    const counterValue = value ? (100 - parseInt(value)).toString() : '';
                                    handleInputChange({
                                        target: {
                                            name: 'counterpartyFaultRate',
                                            value: counterValue || null 
                                        }
                                    });
                                }}
                                placeholder="0-100"
                                invalid={!!errors.ourFaultRate}
                            />
                            <span className="input-group-text">%</span>
                            <FormFeedback>{errors.ourFaultRate}</FormFeedback>
                        </div>
                    </FormGroup>
                </Col>
            </Row>
        </>
    );
};

AccidentInfoForm.propTypes = {
    formData: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    setFormData: PropTypes.func.isRequired,
    handleInputChange: PropTypes.func.isRequired,
    handleLicensePlateSearch: PropTypes.func.isRequired,
    fieldPermissions: PropTypes.array,
    canEdit: PropTypes.bool,
};

export default AccidentInfoForm;
